import {useCallback, useMemo, useState} from 'react';
import {IconButton, Box, Typography} from '@mui/material';
import {DateRangePicker} from '@mui/x-date-pickers-pro';
import {DatePicker} from '@mui/x-date-pickers';
import {Add} from '@mui/icons-material';
import {Dayjs} from 'dayjs';
import {DateRange} from '@mui/lab';
import {messageConfig} from '../../../constants/messages';

export interface ISeasonalDateRange {
  id: number;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  days_of_operation: string[];
  isOneDay?: boolean; // Flag to indicate if this is a one-day entry
}

interface SeasonalDateRangeInputProps {
  existingRanges: ISeasonalDateRange[];
  onAdd: (range: ISeasonalDateRange) => void;
  isOneDay?: boolean;
}

export const SeasonalDateRangeInput = ({
  existingRanges,
  onAdd,
  isOneDay = false,
}: SeasonalDateRangeInputProps) => {
  const [singleDate, setSingleDate] = useState<Dayjs | null>(null);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs | null>>([
    null,
    null,
  ]);

  const handleDateRangeChange = useCallback(
    (newValue: DateRange<Dayjs | null>) => {
      setDateRange(newValue);
    },
    [],
  );

  const handleSingleDateChange = useCallback((date: Dayjs | null) => {
    setSingleDate(date);
  }, []);

  const handleAdd = useCallback(() => {
    if (isOneDay && singleDate) {
      const newRange: ISeasonalDateRange = {
        id: -1 * Date.now(),
        start_date: singleDate,
        end_date: singleDate,
        days_of_operation: [singleDate.format('dddd')], // Auto-populate with the day name
        isOneDay: true,
      };
      onAdd(newRange);
      setSingleDate(null);
    } else if (!isOneDay && dateRange[0] && dateRange[1]) {
      const newRange: ISeasonalDateRange = {
        id: -1 * Date.now(),
        start_date: dateRange[0],
        end_date: dateRange[1],
        days_of_operation: [],
        isOneDay: false,
      };
      onAdd(newRange);
      setDateRange([null, null]);
    }
  }, [dateRange, singleDate, isOneDay, onAdd]);

  // Check if date range is valid to add
  const disableAdd = useMemo(() => {
    if (isOneDay) {
      // For single dates, just check if date is selected
      if (!singleDate) {
        return true;
      }

      // Check if date already exists in OTHER one-day entries only
      const dateExists = existingRanges.some(range => {
        if (!range.start_date || !range.end_date) return false;
        // Only check against one-day entries (where start equals end)
        const isOneDayEntry = range.start_date.isSame(range.end_date, 'day');
        return (
          isOneDayEntry &&
          range.start_date.format('YYYY-MM-DD') ===
            singleDate.format('YYYY-MM-DD')
        );
      });

      return dateExists;
    } else {
      // Both dates must be selected for range
      if (!dateRange[0] || !dateRange[1]) {
        return true;
      }

      // Check for overlapping ranges in OTHER multiple-days entries only
      const isOverlapping = existingRanges.some(range => {
        if (!range.start_date || !range.end_date) return false;
        // Only check against multiple-days entries (where start doesn't equal end)
        const isMultipleDaysEntry = !range.start_date.isSame(
          range.end_date,
          'day',
        );
        if (!isMultipleDaysEntry) return false;

        // Check if any endpoint of the new range falls within an existing range
        const newRangeEndpoints = [dateRange[0]!, dateRange[1]!];
        const existingRangeEndpoints = [range.start_date, range.end_date];

        return (
          newRangeEndpoints.some(endpoint =>
            endpoint.isBetween(range.start_date, range.end_date, 'd', '[]'),
          ) ||
          existingRangeEndpoints.some(endpoint =>
            endpoint.isBetween(dateRange[0], dateRange[1], 'd', '[]'),
          )
        );
      });

      return isOverlapping;
    }
  }, [dateRange, singleDate, existingRanges, isOneDay]);

  return (
    <Box className='mb-4'>
      <Typography className='font-bold text-16 mb-2'>
        {isOneDay
          ? messageConfig.participationForm.seasonalDateRange.addIndividualDates
          : messageConfig.participationForm.seasonalDateRange
              .addSeasonalDateRanges}
      </Typography>
      <Typography className='text-14 text-white-50 mb-3'>
        {isOneDay
          ? messageConfig.participationForm.seasonalDateRange.descriptionOneDay
          : messageConfig.participationForm.seasonalDateRange
              .descriptionMultipleDays}
      </Typography>
      <Box className='flex items-center gap-3'>
        {isOneDay ? (
          <DatePicker
            label='Select Date'
            value={singleDate}
            onChange={handleSingleDateChange}
          />
        ) : (
          <DateRangePicker
            localeText={{start: 'Start Date', end: 'End Date'}}
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        )}
        <IconButton
          className='ml-2 w-40 h-40 font-bold radius-5'
          sx={{
            background: `var(--blue-variant10)`,
            border: `1px solid var(--blue_variant40)`,
          }}
          aria-label='add-date-range'
          disabled={disableAdd}
          onClick={handleAdd}>
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
};
